import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axios';
import { Pencil, Trash2, ChevronDown, Search, MessageCircle, Download } from 'lucide-react';
import './UserManagement.css';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';

const formatCreatedAt = (user) => {
  const val = user.createdAt ?? user.created_at ?? user.createdat;
  if (!val || isNaN(new Date(val).getTime())) return '-';
  return new Date(val).toLocaleDateString();
};

/** Stable string id for comparing session user to row user (avoids string/number mismatch). */
const userIdKey = (id) => (id == null || id === '' ? '' : String(id));

const UserManagement = ({ users, onCreateUser, onUpdateUser, onDeleteUser, canCreatePowerUser, onRefresh, onChatUser, canStartChat, isAdmin }) => {
  const { user: authUser } = useAuth();
  const editFormRef = useRef(null);
  const nameInputRef = useRef(null);
  const postSubmitMessageRef = useRef(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'admin',
    reportsTo: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [search, setSearch] = useState('');
  const [usersExporting, setUsersExporting] = useState(false);

  // After update/create, success renders above the table; scroll it into view so it is not missed when the admin was scrolled to the table.
  useEffect(() => {
    if (!success || showCreateForm) return;
    const id = window.requestAnimationFrame(() => {
      try {
        postSubmitMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {
        /* ignore */
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [success, showCreateForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (editingUser) {
      const passwordProvided = !!(formData.password && String(formData.password).trim() !== '');
      const updateData = { ...formData };
      if (!updateData.password || updateData.password === '') {
        delete updateData.password;
      }
      const result = await onUpdateUser(editingUser.id, updateData);
      if (result.success) {
        const authKey = userIdKey(authUser?.id ?? authUser?.userId);
        const editingKey = userIdKey(editingUser?.id);
        // Same logic as before (required auth id + not editing self), but ids compared as strings so number/string mismatch cannot hide the password message.
        const passwordSetForAnotherUser =
          passwordProvided && authKey !== '' && editingKey !== authKey;
        setSuccess(
          passwordSetForAnotherUser
            ? 'Password updated. The user will be asked to change it at next login.'
            : 'User updated successfully'
        );
        setFormData({ email: '', password: '', name: '', role: 'admin', reportsTo: '' });
        setEditingUser(null);
        setShowCreateForm(false);
        setTimeout(() => window.location.reload(), 4000);
      } else {
        setError(result.error);
      }
    } else {
      const result = await onCreateUser(formData);
      if (result.success) {
        setSuccess('User created successfully');
        setFormData({ email: '', password: '', name: '', role: 'admin', reportsTo: '' });
        setShowCreateForm(false);
        setTimeout(() => window.location.reload(), 4000);
      } else {
        setError(result.error);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      reportsTo: user.reportsTo || ''
    });
    setShowCreateForm(true);
    setError('');
    setSuccess('');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        try {
          editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (_) {
          /* ignore */
        }
        try {
          nameInputRef.current?.focus();
        } catch (_) {
          /* ignore */
        }
      });
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({ email: '', password: '', name: '', role: 'admin', reportsTo: '' });
    setError('');
    setSuccess('');
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const result = await onDeleteUser(userId);
      if (result.success) {
        // Refresh handled by parent
      } else {
        alert(result.error);
      }
    }
  };

  const handleExportUsersExcel = async () => {
    setUsersExporting(true);
    setError('');
    try {
      const base = axiosInstance.defaults.baseURL || '';
      const url = base + '/api/tenant/users/export-excel';
      const token = localStorage.getItem('token');
      const fetchHeaders = { Authorization: 'Bearer ' + token };
      if (typeof window !== 'undefined' && window.location.host) {
        fetchHeaders['X-Tenant-Host'] = window.location.host;
      }
      const res = await fetch(url, { headers: fetchHeaders });
      if (!res.ok) {
        let errData = null;
        try {
          errData = await res.json();
        } catch (_) {}
        setError(
          getFriendlyErrorMessage(
            { response: { status: res.status, data: errData || { code: 'EXPORT_FAILED' } } },
            { kind: 'export' }
          ) || EXPORT_FAILED_MESSAGE
        );
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'Users_Export_' + new Date().toISOString().split('T')[0] + '.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error(err);
      setError(getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE);
    } finally {
      setUsersExporting(false);
    }
  };

  const getRoleDisplay = (role) => {
    const roleMap = { power_user: 'Power User', admin: 'Admin', manager: 'Manager' };
    return roleMap[role] || role;
  };

  const getRolePriority = (role) => {
    const rolePriority = { power_user: 1, admin: 2, manager: 3 };
    return rolePriority[role] || 99;
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name.split(/\s+/).map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedUsers = () => {
    let sorted = [...users];

    if (search.trim()) {
      const q = search.toLowerCase();
      sorted = sorted.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        getRoleDisplay(u.role).toLowerCase().includes(q)
      );
    }

    if (!sortColumn) {
      return sorted.sort((a, b) => {
        const pa = getRolePriority(a.role);
        const pb = getRolePriority(b.role);
        if (pa !== pb) return pa - pb;
        return a.name.localeCompare(b.name);
      });
    }

    return sorted.sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'user':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'role':
          aValue = getRolePriority(a.role);
          bValue = getRolePriority(b.role);
          break;
        case 'reportsTo':
          const rtA = a.reportsTo ? users.find(u => u.id === a.reportsTo) : null;
          const rtB = b.reportsTo ? users.find(u => u.id === b.reportsTo) : null;
          aValue = (rtA?.name || '').toLowerCase();
          bValue = (rtB?.name || '').toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => (
    <th className="um-sortable-th" onClick={() => handleSort(column)} title={'Sort by ' + children}>
      <button type="button" className="um-sort-btn">
        <span>{children}</span>
        <ChevronDown size={10} className={'um-sort-icon' + (sortColumn === column ? ' active' : '') + (sortColumn === column && sortDirection === 'desc' ? ' desc' : '')} />
      </button>
    </th>
  );

  const sortedUsers = getSortedUsers();
  const admins = users.filter(u => u.role === 'admin' || u.role === 'power_user');
  const managers = users.filter(u => u.role === 'manager');

  return (
    <div>
      <div className="um-section-header">
        <div>
          <h2 className="um-section-title">User Management</h2>
          <p className="um-section-subtitle">Manage users, roles and access</p>
        </div>
        <div className="um-section-header-actions">
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary um-export-btn"
              onClick={handleExportUsersExcel}
              disabled={usersExporting}
              title="Download all users as Excel"
            >
              <Download size={16} aria-hidden />
              {usersExporting ? 'Exporting…' : 'Export to Excel'}
            </button>
          )}
          <button className="btn btn-primary um-create-btn" onClick={() => {
            if (showCreateForm) { handleCancel(); } else { setShowCreateForm(true); setEditingUser(null); setFormData({ email: '', password: '', name: '', role: 'admin', reportsTo: '' }); }
          }}>
            {showCreateForm ? 'Cancel' : '+ Create User'}
          </button>
        </div>
      </div>

      <div className="um-stats-row">
        <div className="um-stat-card">
          <span className="um-stat-label">TOTAL USERS</span>
          <span className="um-stat-value um-stat-total">{users.length}</span>
        </div>
        <div className="um-stat-card">
          <span className="um-stat-label">ADMINS</span>
          <span className="um-stat-value um-stat-admins">{admins.length}</span>
        </div>
        <div className="um-stat-card">
          <span className="um-stat-label">MANAGERS</span>
          <span className="um-stat-value um-stat-managers">{managers.length}</span>
        </div>
      </div>

      {showCreateForm && (
        <form ref={editFormRef} onSubmit={handleSubmit} className="create-form card um-user-form-panel">
          {editingUser && <h4>Edit User: {editingUser.name}</h4>}
          <div className="form-group">
            <label>Name</label>
            <input ref={nameInputRef} type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Password {editingUser && '(leave blank to keep current)'}</label>
            <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingUser} placeholder={editingUser ? 'Leave blank to keep current password' : ''} />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value, reportsTo: '' })} required>
              {canCreatePowerUser && <option value="power_user">Power User</option>}
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          {formData.role === 'manager' && (
            <div className="form-group">
              <label>Reports To (Optional)</label>
              <select value={formData.reportsTo} onChange={(e) => setFormData({ ...formData, reportsTo: e.target.value })}>
                <option value="">None (No reporting relationship)</option>
                {admins.map(admin => (<option key={admin.id} value={admin.id}>{admin.name} ({getRoleDisplay(admin.role)})</option>))}
              </select>
              <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-muted)' }}>Select an Admin or Power User that this manager reports to.</small>
            </div>
          )}
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          <button type="submit" className="btn btn-primary">{editingUser ? 'Update User' : 'Create User'}</button>
        </form>
      )}

      {!showCreateForm && error && (
        <div ref={postSubmitMessageRef} className="error" style={{ marginBottom: '20px' }} role="status">
          {error}
        </div>
      )}
      {!showCreateForm && success && (
        <div ref={postSubmitMessageRef} className="success" style={{ marginBottom: '20px' }} role="status" tabIndex={-1}>
          {success}
        </div>
      )}



      <div className="card um-table-card">
        <div className="um-search-bar">
          <Search size={15} className="um-search-icon" />
          <input type="text" placeholder="Search by name, email or role\u2026" className="um-search-input" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button type="button" onClick={() => setSearch('')} className="um-search-clear">Clear</button>}
        </div>
        <div className="table-container">
          <table className="table um-table">
            <thead>
              <tr>
                <SortableHeader column="user">User</SortableHeader>
                <SortableHeader column="role">Role</SortableHeader>
                <SortableHeader column="reportsTo">Reports To</SortableHeader>
                <SortableHeader column="created">Created</SortableHeader>
                <th className="um-actions-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="um-empty-state">
                    {users.length === 0 ? <p>No users found.</p> : <p>No users match your search.</p>}
                  </td>
                </tr>
              ) : (
                sortedUsers.map(user => {
                  const reportsToUser = user.reportsTo ? users.find(u => u.id === user.reportsTo) : null;
                  const isAdmin = user.role === 'admin' || user.role === 'power_user';
                  return (
                    <tr key={user.id} className="um-row">
                      <td className="um-user-cell">
                        <div className="um-user-info">
                          <div className={'um-avatar ' + (isAdmin ? 'um-avatar-admin' : 'um-avatar-manager')}>
                            {getInitials(user.name)}
                          </div>
                          <div className="um-user-text">
                            <span className="um-user-name">{user.name}</span>
                            <a href={'mailto:' + user.email} className="um-user-email">{user.email}</a>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={'um-role-badge ' + (isAdmin ? 'um-role-admin' : 'um-role-manager')}>
                          <span className="um-role-dot"></span>
                          {getRoleDisplay(user.role)}
                        </span>
                      </td>
                      <td className="um-meta-cell">
                        {user.role === 'manager' && reportsToUser
                          ? reportsToUser.name + ' (' + getRoleDisplay(reportsToUser.role) + ')'
                          : '-'}
                      </td>
                      <td className="um-meta-cell um-date-cell">{formatCreatedAt(user)}</td>
                      <td>
                        <div className="um-actions">
                          {canStartChat && (user.role === 'admin' || user.role === 'manager') && onChatUser && (
                            <button type="button" className="um-action-btn um-action-chat" onClick={() => onChatUser(user)} title="Chat">
                              <MessageCircle size={14} />
                            </button>
                          )}
                          {onUpdateUser && (
                            <button type="button" className="um-action-btn um-action-edit" onClick={() => handleEdit(user)} title="Edit user">
                              <Pencil size={14} />
                            </button>
                          )}
                          {onDeleteUser && (
                            <button type="button" className="um-action-btn um-action-delete" onClick={() => handleDelete(user.id)} title="Delete user">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;

